/**
 * Role Service
 * CRUD for roles and permissions, seeded from mockData rolePermissions
 */

import { rolePermissions } from '../../data/mockData';

const STORAGE_KEY = 'medfast_roles';

class RoleService {
  constructor() {
    this._ensureSeed();
  }

  _ensureSeed() {
    try {
      const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (existing && existing.length > 0) return;

      const seeded = Object.entries(rolePermissions).map(([key, value]) => ({
        id: key,
        slug: key,
        name: value.name || key,
        description: value.description || '',
        permissions: value.permissions || [],
        tabs: value.tabs || [],
        isSystem: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
    } catch (err) {
      console.error('RoleService seed error:', err);
    }
  }

  _getAll() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  }

  _save(roles) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  }

  async getAll() {
    try {
      return { success: true, data: this._getAll() };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getById(roleId) {
    try {
      const role = this._getAll().find(r => r.id === roleId);
      if (!role) return { success: false, error: 'Role not found' };
      return { success: true, data: role };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async create(roleData) {
    try {
      const roles = this._getAll();
      const slug = roleData.slug || roleData.name.toLowerCase().replace(/\s+/g, '_');
      if (roles.find(r => r.id === slug || r.slug === slug)) {
        return { success: false, error: 'Role with this slug already exists' };
      }
      const now = new Date().toISOString();
      const newRole = {
        id: slug,
        slug,
        name: roleData.name || slug,
        description: roleData.description || '',
        permissions: roleData.permissions || [],
        tabs: roleData.tabs || [],
        isSystem: false,
        createdAt: now,
        updatedAt: now
      };
      roles.push(newRole);
      this._save(roles);
      return { success: true, data: newRole };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async update(roleId, updates) {
    try {
      const roles = this._getAll();
      const idx = roles.findIndex(r => r.id === roleId);
      if (idx === -1) return { success: false, error: 'Role not found' };

      roles[idx] = {
        ...roles[idx],
        ...updates,
        id: roles[idx].id,
        slug: roles[idx].slug,
        isSystem: roles[idx].isSystem,
        updatedAt: new Date().toISOString()
      };
      this._save(roles);
      return { success: true, data: roles[idx] };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async delete(roleId) {
    try {
      const roles = this._getAll();
      const role = roles.find(r => r.id === roleId);
      if (!role) return { success: false, error: 'Role not found' };
      if (role.isSystem) return { success: false, error: 'Cannot delete system roles' };

      this._save(roles.filter(r => r.id !== roleId));
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  getAllPermissions() {
    const perms = new Set();
    for (const role of Object.values(rolePermissions)) {
      (role.permissions || []).forEach(p => perms.add(p));
    }
    return [...perms].sort();
  }

  getAllTabs() {
    const tabs = new Set();
    for (const role of Object.values(rolePermissions)) {
      (role.tabs || []).forEach(t => tabs.add(t));
    }
    return [...tabs].sort();
  }
}

export default new RoleService();
